/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { ColumnDef, Row } from '@tanstack/react-table'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid'

import { DATE_FORMATS } from 'constants/DateFormats'
import Dates from 'utils/Dates'
import { currencyNumberFormat, driverName, numberRangeColumnFilter } from 'utils/utility'
import { getShortNameFromId } from 'utils/reservationUtils'
import { Reservation } from 'types/entities/Reservation'
import UiTableHeaderLabel from 'components/Ui/UiTableHeaderLabel'
import { UiTableColumnVisibilityToggle } from 'components/Ui/UiTableColumnVisibilityToggle'
import UiPackagePill from 'components/Ui/UiPackagePill'
import UiReservationStatusPill from 'components/Ui/UiReservationStatusPill'
import UiInlineSelect from 'components/Ui/UiInlineSelect'
import UiInlineSelectPill from 'components/Ui/UiInlineSelectPill'
import { useSellers } from 'utils/sellerUtils'
import { useCurrentUser } from 'utils/hooks/useCurrentUser'
import { UiTableCellTextPrimary } from 'components/Ui/UiTableCellText'
import { getPackageCategoryClassName } from 'utils/packageUtils'
import { getCompanyDisplayName } from 'utils/companyUtils'
import UiCompanyTypePill from 'components/Ui/UiCompanyTypePill'
import { tableActionsMeta } from 'utils/table/tableColumnWidths'
import { exportColumn, exportField } from 'utils/table/tableColumnExport'
import { useAppDispatch, AppDispatch } from 'helpers/store'
import ReservationsAction from 'store/reservations/ReservationsAction'
import { setReservation } from 'store/reservations/ReservationsReducer'
import { MassActionType } from 'types/enums/specific/MassAction'
import { ReservationStatus } from 'types/enums/ReservationStatus'
import { useReservationStatusOptions } from 'utils/reservationUtils'
import { UserType } from 'types/enums/UserType'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { createAssignmentReminderForEntity } from 'utils/reminders/createAssignmentReminder'

export const useBookingSellerReservationsColumns = (
    onDeleteItemHandler: (reservationId: number) => void,
    options?: {
        readOnly?: boolean
        detailPathPrefix?: string
        isOnlineOnly?: boolean
        onInlinePatch?: (reservationId: number, patch: Partial<Reservation>) => void
    }
): ColumnDef<Reservation>[] => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { sellerById, selectOptions: sellerOptions } = useSellers()
    const { user } = useCurrentUser()
    const readOnly = options?.readOnly ?? false
    const isOnlineOnly = options?.isOnlineOnly ?? false
    // const hideRevenue = user?.user_type === UserType.Seller
    const hideRevenue = false;
    const detailPathPrefix = options?.detailPathPrefix ?? '/reservations/detail'
    const onInlinePatch = options?.onInlinePatch
    const { options: reservationStatusOptions } = useReservationStatusOptions(isOnlineOnly)

    const statusOptions = useMemo(
        () =>
            reservationStatusOptions
                .filter((option) => option.value !== ReservationStatus.All)
                .map((option) => ({
                    value: option.value as ReservationStatus,
                    label: option.label,
                })),
        [reservationStatusOptions]
    )

    const patchReservation = useCallback(
        async (
            reservation: Reservation,
            action: MassActionType,
            params: Record<string, unknown>,
            localPatch: Partial<Reservation>
        ) => {
            await dispatch(
                ReservationsAction.fetchMassAction({
                    ids: [reservation.id],
                    action,
                    params,
                })
            )
            dispatch(setReservation({ ...reservation, ...localPatch }))
            onInlinePatch?.(reservation.id, localPatch)

            if (
                action === MassActionType.AssignSeller &&
                localPatch.seller_id != null &&
                Number(localPatch.seller_id) !== Number(reservation.seller_id)
            ) {
                await createAssignmentReminderForEntity(dispatch as unknown as AppDispatch, {
                    modelName: ReminderModelName.Reservation,
                    modelId: Number(reservation.id),
                    sellerId: Number(localPatch.seller_id),
                    previousSellerId: reservation.seller_id,
                    hallId: reservation.hall_id,
                    content: t('Assigned to you') as string,
                })
            }
        },
        [dispatch, onInlinePatch, t]
    )

    return useMemo(
        () => [
            {
                accessorKey: 'id',
                meta: exportColumn(exportField('id', 'ID')),
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('ID')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => <span>{row.original.id}</span>,
            },
            ...(!isOnlineOnly
                ? [
                      {
                          accessorKey: 'seller_id',
                          meta: exportColumn(exportField('seller.name', 'Created by')),
                          enableSorting: true,
                          enableColumnFilter: true,
                          header: () => <UiTableHeaderLabel>{t('Created by')}</UiTableHeaderLabel>,
                          cell: ({ row }: { row: Row<Reservation> }) => {
                              const reservation = row.original
                              if (readOnly) {
                                  const seller = sellerById(reservation.seller_id)
                                  return (
                                      <UiTableCellTextPrimary>
                                          {seller ? driverName(seller, true) : '-'}
                                      </UiTableCellTextPrimary>
                                  )
                              }

                              return (
                                  <UiInlineSelect
                                      ariaLabel={t('Created by') as string}
                                      value={reservation.seller_id}
                                      options={sellerOptions.map((option) => ({
                                          value: option.value,
                                          label: option.label,
                                      }))}
                                      onSave={(sellerId) =>
                                          patchReservation(
                                              reservation,
                                              MassActionType.AssignSeller,
                                              { seller_id: Number(sellerId) },
                                              { seller_id: Number(sellerId) }
                                          )
                                      }
                                  />
                              )
                          },
                      },
                      {
                          accessorKey: 'offer_number',
                          meta: exportColumn(exportField('offer_number', 'Offer #')),
                          enableSorting: true,
                          enableColumnFilter: true,
                          header: () => <UiTableHeaderLabel>{t('Offer #')}</UiTableHeaderLabel>,
                          cell: ({ row }: { row: Row<Reservation> }) => (
                              <UiTableCellTextPrimary>{row.original.offer_number}</UiTableCellTextPrimary>
                          ),
                      },
                  ]
                : []),
            {
                accessorKey: 'created_at',
                meta: exportColumn(exportField('created_at', 'Created')),
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Created')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    return (
                        <UiTableCellTextPrimary>
                            {reservation.created_at
                                ? Dates.date(reservation.created_at, '', DATE_FORMATS.DATE_TIME_DOT)
                                : '---'}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'updated_at',
                meta: exportColumn(exportField('updated_at', 'Updated')),
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Updated')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    return (
                        <UiTableCellTextPrimary>
                            {reservation.updated_at
                                ? Dates.date(reservation.updated_at, '', DATE_FORMATS.DATE_TIME_DOT)
                                : '---'}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'company_id',
                meta: exportColumn(exportField('company.name', 'Company')),
                accessorFn: (row: Reservation) =>
                    row?.packagesCart?.customer?.name || getCompanyDisplayName(row?.company) || '',
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Company')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    return (
                        <UiTableCellTextPrimary className={clsx('BookingSellerReservationsCompanyCell', 'font-medium')}>
                            <div
                                className={clsx(
                                    'BookingSellerReservationsCompanyName',
                                    'inline-flex w-full leading-tight break-words'
                                )}
                            >
                                {reservation?.packagesCart?.customer?.name ||
                                    getCompanyDisplayName(reservation?.company) ||
                                    '---'}
                            </div>
                            {reservation?.packagesCart?.customer?.first_name && (
                                <div
                                    className={clsx(
                                        'BookingSellerReservationsCompanyCustomer',
                                        'flex w-full gap-x-1 leading-tight'
                                    )}
                                >
                                    <span>{reservation.packagesCart.customer.first_name}</span>
                                    <span>{reservation.packagesCart.customer.last_name}</span>
                                </div>
                            )}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorFn: (row: Reservation) => row.company?.type ?? null,
                id: 'company_type',
                meta: exportColumn(exportField('company.type', 'Account Type')),
                enableSorting: false,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Account Type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const companyType = row.original.company?.type

                    if (!companyType) {
                        return <UiTableCellTextPrimary>-</UiTableCellTextPrimary>
                    }

                    return <UiCompanyTypePill type={companyType} />
                },
            },
            {
                accessorKey: 'package',
                meta: exportColumn(exportField('category', 'Ticket type')),
                accessorFn: (row: Reservation) => row?.packagesCart?.package?.name || row?.category || '',
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Ticket type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    const packageName = reservation?.category || ''
                    return (
                        <UiTableCellTextPrimary>
                            <UiPackagePill
                                className={getPackageCategoryClassName(packageName)}
                                value={getShortNameFromId(packageName)}
                            />
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'date',
                meta: exportColumn(exportField('date', 'Date')),
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Date')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    return (
                        <UiTableCellTextPrimary>
                            {reservation.date ? Dates.date(reservation.date, '', DATE_FORMATS.DATE_DOT) : '---'}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'time',
                meta: exportColumn(exportField('hour', 'Time'), exportField('minute', 'Time')),
                accessorFn: (row: Reservation) => {
                    if (row.time) {
                        return row.time
                    }
                    if (row.hour !== undefined && row.minute !== undefined) {
                        return `${row.hour}:${String(row.minute).padStart(2, '0')}`
                    }
                    return ''
                },
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Time')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    const time =
                        reservation.time ||
                        (reservation.hour !== undefined && reservation.minute !== undefined
                            ? `${reservation.hour}:${String(reservation.minute).padStart(2, '0')}`
                            : null)
                    return <UiTableCellTextPrimary>{time || '---'}</UiTableCellTextPrimary>
                },
            },
            {
                accessorKey: 'persons',
                meta: exportColumn(exportField('persons', 'Persons')),
                enableSorting: true,
                enableColumnFilter: true,
                filterFn: numberRangeColumnFilter,
                header: () => <UiTableHeaderLabel>{t('Persons')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    return (
                        <UiTableCellTextPrimary
                            className={clsx('BookingSellerReservationsPersonsCell', 'text-center w-full')}
                        >
                            {reservation.persons || 0}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'status',
                meta: exportColumn(exportField('status', 'Status')),
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('Status')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<Reservation> }) => {
                    const reservation = row.original
                    if (readOnly) {
                        return <UiReservationStatusPill status={reservation.status || ''} />
                    }

                    return (
                        <UiInlineSelectPill<ReservationStatus>
                            ariaLabel={t('Status') as string}
                            value={(reservation.status || ReservationStatus.Confirmed) as ReservationStatus}
                            options={statusOptions}
                            renderPill={(status) => <UiReservationStatusPill status={status} />}
                            onChange={(status) =>
                                patchReservation(
                                    reservation,
                                    MassActionType.ChangeStatus,
                                    { status },
                                    { status }
                                )
                            }
                        />
                    )
                },
            },
            ...(!hideRevenue
                ? [
                      {
                          accessorKey: 'total',
                          meta: exportColumn(exportField('price', 'Reservierter Umsatz')),
                          accessorFn: (row: Reservation) => {
                              const priceString = row?.price || '0'
                              return parseFloat(String(priceString))
                          },
                          enableSorting: true,
                          enableColumnFilter: true,
                          filterFn: numberRangeColumnFilter,
                          header: () => <UiTableHeaderLabel>{t('Reservierter Umsatz')}</UiTableHeaderLabel>,
                          cell: ({ row }: { row: Row<Reservation> }) => {
                              const reservation = row.original
                              const priceString = reservation?.price || '0'
                              const total = parseFloat(String(priceString))
                              return <span>{currencyNumberFormat(total)}</span>
                          },
                      },
                  ]
                : []),
            ...(!isOnlineOnly && !hideRevenue
                ? [
                      {
                          accessorKey: 'total_invoice',
                          meta: exportColumn(exportField('total_invoice', 'Effektiver Umsatz')),
                          accessorFn: (row: Reservation) => {
                              const priceString = row?.total_invoice || '0'
                              return parseFloat(String(priceString))
                          },
                          enableSorting: true,
                          enableColumnFilter: true,
                          filterFn: numberRangeColumnFilter,
                          header: () => <UiTableHeaderLabel>{t('Effektiver Umsatz')}</UiTableHeaderLabel>,
                          cell: ({ row }: { row: Row<Reservation> }) => {
                              const reservation = row.original
                              const priceString = reservation?.total_invoice || '0'
                              const total = parseFloat(String(priceString))
                              return <span>{currencyNumberFormat(total)}</span>
                          },
                      },
                  ]
                : []),
            ...(!readOnly
                ? [
                      {
                          accessorKey: 'actions',
                          ...tableActionsMeta(2),
                          enableSorting: false,
                          enableColumnFilter: false,
                          header: ({ table }) => (
                              <div className={clsx('flex flex-row items-center leading-tight justify-end h-full')}>
                                  <UiTableColumnVisibilityToggle table={table} />
                              </div>
                          ),
                          cell: ({ row }: { row: Row<Reservation> }) => {
                              const reservation = row.original
                              return (
                                  <div
                                      className={clsx(
                                          'BookingSellerReservationsActionsCell',
                                          'w-full text-right flex flex-row gap-x-2 justify-end'
                                      )}
                                  >
                                      <Link
                                          to={`${detailPathPrefix}/${reservation.id}`}
                                          className={clsx(
                                              'BookingSellerReservationsEditLink',
                                              'rounded-md px-2 py-1.5 border border-blue-500 bg-blue-500 active:bg-opacity-80 focus:bg-opacity-80 hover:bg-opacity-90 text-white flex w-full md:w-auto items-center text-center justify-center leading-6 font-bold cursor-pointer'
                                          )}
                                          aria-label={t('Edit reservation')}
                                      >
                                          <PencilIcon
                                              className={clsx(
                                                  'BookingSellerReservationsActionIcon',
                                                  'w-4 h-4 flex-none'
                                              )}
                                          />
                                      </Link>
                                      <button
                                          type="button"
                                          className={clsx(
                                              'BookingSellerReservationsDeleteButton',
                                              'bg-red-500 hover:bg-red-600 text-white px-2 py-1.5 rounded-md cursor-pointer flex items-center justify-center'
                                          )}
                                          onClick={() => onDeleteItemHandler(reservation.id)}
                                          aria-label={t('Delete reservation')}
                                      >
                                          <TrashIcon
                                              className={clsx(
                                                  'BookingSellerReservationsActionIcon',
                                                  'w-4 h-4 flex-none'
                                              )}
                                          />
                                      </button>
                                  </div>
                              )
                          },
                      } satisfies ColumnDef<Reservation>,
                  ]
                : []),
        ],
        [
            isOnlineOnly,
            readOnly,
            hideRevenue,
            t,
            sellerById,
            sellerOptions,
            statusOptions,
            detailPathPrefix,
            onDeleteItemHandler,
            patchReservation,
        ]
    )
}
